import { useState, useEffect } from 'react';
import type { CreatorProfile } from '@/mocks/profiles';
import SAR from '@/components/feature/SAR';
import SARIcon from '@/components/feature/SARIcon';

interface SupportModalProps {
  profile: CreatorProfile;
  isOpen: boolean;
  onClose: () => void;
}

const presetAmounts = [50, 100, 200, 500, 1000];
const paymentMethods = [
  { id: 'apple_pay', label: 'Apple Pay', icon: 'ri-apple-line' },
  { id: 'mada', label: 'مدى', icon: 'ri-bank-card-line' },
  { id: 'stc_pay', label: 'STC Pay', icon: 'ri-smartphone-line' },
  { id: 'visa', label: 'Visa / Mastercard', icon: 'ri-visa-line' },
];

const SupportModal = ({ profile, isOpen, onClose }: SupportModalProps) => {
  const [amount, setAmount] = useState<number | ''>('');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('mada');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [message, setMessage] = useState('');
  const [step, setStep] = useState<'amount' | 'payment' | 'success'>('amount');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep('amount');
      setAmount('');
      setSelectedPreset(null);
      setMessage('');
      setIsAnonymous(false);
      setPaymentMethod('mada');
    }, 300);
  };

  const handlePreset = (val: number) => {
    setSelectedPreset(val);
    setAmount(val);
  };

  const handleProceed = () => {
    if (!amount || Number(amount) < 1) return;
    setStep('payment');
  };

  const handlePay = () => {
    setStep('success');
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      dir="rtl"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden animate-fade-in">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <div className="flex items-center gap-3">
            <img
              src={profile.avatar}
              alt={profile.displayName}
              className="w-10 h-10 rounded-xl object-cover object-top border-2 border-stone-100"
            />
            <div>
              <p className="text-xs text-stone-400">دعم المبدع</p>
              <p className="font-bold text-stone-800 text-sm">{profile.displayName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-500 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-6">

          {/* Success State */}
          {step === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-4">
                <i className="ri-check-double-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="font-black text-stone-800 text-xl mb-2">شكراً لدعمك!</h3>
              <p className="text-stone-500 text-sm mb-1">
                تم استلام دعمك لـ <strong>{profile.displayName}</strong>
              </p>
              <p className="text-stone-500 text-sm mb-6">
                بمبلغ <strong className="text-emerald-600"><SAR amount={Number(amount)} iconSize={13} /></strong>. جزاك الله خيراً.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setStep('amount'); setAmount(''); setSelectedPreset(null); setMessage(''); }}
                  className="flex-1 py-3 rounded-xl bg-stone-100 text-stone-700 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-stone-200 transition-colors"
                >
                  دعم مرة أخرى
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors"
                >
                  إغلاق
                </button>
              </div>
            </div>
          )}

          {/* Amount Step */}
          {step === 'amount' && (
            <>
              <p className="text-sm font-semibold text-stone-700 mb-3">اختر مبلغ الدعم (ريال)</p>

              {/* Preset Amounts */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {presetAmounts.map((val) => (
                  <button
                    key={val}
                    onClick={() => handlePreset(val)}
                    className={`py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      selectedPreset === val
                        ? 'bg-emerald-600 text-white'
                        : 'bg-stone-50 text-stone-700 border border-stone-200 hover:border-emerald-300'
                    }`}
                  >
                    {val}
                  </button>
                ))}
                <button
                  onClick={() => { setSelectedPreset(null); setAmount(''); }}
                  className={`py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    selectedPreset === null && amount !== ''
                      ? 'bg-emerald-600 text-white'
                      : 'bg-stone-50 text-stone-700 border border-stone-200 hover:border-emerald-300'
                  }`}
                >
                  مخصص
                </button>
              </div>

              {/* Custom Amount */}
              <div className="relative mb-4">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value ? Number(e.target.value) : '');
                    setSelectedPreset(null);
                  }}
                  placeholder="أدخل مبلغاً مخصصاً"
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 text-right"
                  min={1}
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"><SARIcon size={14} /></span>
              </div>

              {/* Message */}
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="اترك رسالة تشجيعية للمبدع (اختياري)"
                rows={2}
                maxLength={200}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-700 focus:outline-none focus:border-emerald-400 resize-none mb-3 text-right"
              />

              {/* Anonymous */}
              <button
                onClick={() => setIsAnonymous(!isAnonymous)}
                className="flex items-center gap-2 mb-5 cursor-pointer"
              >
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isAnonymous ? 'bg-emerald-600 border-emerald-600' : 'border-stone-300'}`}>
                  {isAnonymous && <i className="ri-check-line text-white text-xs"></i>}
                </div>
                <span className="text-sm text-stone-600">دعم بشكل مجهول</span>
              </button>

              <button
                onClick={handleProceed}
                disabled={!amount || Number(amount) < 1}
                className="w-full py-3.5 rounded-xl bg-emerald-600 text-white font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-heart-line ml-1.5"></i>
                ادعم الآن
                {amount ? <> - <SAR amount={Number(amount)} iconSize={12} /></> : ''}
              </button>
            </>
          )}

          {/* Payment Step */}
          {step === 'payment' && (
            <>
              <div className="flex items-center gap-2 mb-5">
                <button
                  onClick={() => setStep('amount')}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 cursor-pointer hover:bg-stone-200 transition-colors"
                >
                  <i className="ri-arrow-right-line text-stone-600"></i>
                </button>
                <span className="font-bold text-stone-800">اختر طريقة الدفع</span>
              </div>

              <div className="mb-4 p-3 bg-emerald-50 rounded-xl flex items-center justify-between">
                <span className="text-sm text-stone-600">المبلغ</span>
                <span className="font-black text-emerald-700"><SAR amount={Number(amount)} iconSize={14} /></span>
              </div>

              <div className="flex flex-col gap-2 mb-5">
                {paymentMethods.map((pm) => (
                  <button
                    key={pm.id}
                    onClick={() => setPaymentMethod(pm.id)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer ${
                      paymentMethod === pm.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${pm.icon} text-xl text-stone-600`}></i>
                    </div>
                    <span className="text-sm font-medium text-stone-700">{pm.label}</span>
                    {paymentMethod === pm.id && (
                      <i className="ri-checkbox-circle-line text-emerald-600 mr-auto"></i>
                    )}
                  </button>
                ))}
              </div>

              <button
                onClick={handlePay}
                className="w-full py-3.5 rounded-xl bg-emerald-600 text-white font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-emerald-700 transition-colors"
              >
                <i className="ri-lock-line ml-1"></i>
                إتمام الدفع الآن
              </button>
              <p className="text-center text-xs text-stone-400 mt-2">
                <i className="ri-shield-check-line text-emerald-500 ml-1"></i>
                دفع آمن ومشفر بالكامل
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportModal;
